// ── 상황판 직원 본인 위젯 데이터 훅 (2026-06-06 정리) ───────────────────────
//
// 본인 위젯 총 10개. visibleSet 에 포함된 경우에만 fetch.
//   출퇴근 토글  /api/attendance/today
//   남은 연차     /api/my/leave-balance
//   일일콜 요약   /api/my/daily-call-summary?days=10
//   오늘 콜       /api/my/widgets/call-today
//   이번달 계약   /api/my/widgets/contracts-month
//   오늘+내일 미팅 /api/my/widgets/meeting
//   추천 지역     /api/my/widgets/region
//   정산 받을돈(파인) /api/my/settlement-owed/pine
//   정산 받을돈(DOT)  /api/my/settlement-owed/dot
//   정산 받을돈(LNJ)  /api/my/settlement-owed/lnj
//
// 출퇴근 위젯 토글은 API 호출 + 응답 후 다시 fetch 해서 상태 동기화.
//
// 항상 로그인한 사람 **본인** 데이터다 (2026-09-15). 전에는 사이드바 "…열람" 줄에서 고른 직원
//   (user_preferences.admin_drilldown_user_by_menu)을 위젯에도 적용했는데, 열람 줄을 없애면서
//   바꿀 방법 없이 옛 선택이 남지 않도록 연결을 끊었다(사용자 확정). 저장값은 DB 에 남아도 읽지 않는다.

import { useCallback, useEffect, useState } from 'react';
import { fetchCurrentAddress } from '../../dashboard/dashboardGeo';
import { apiClient } from '../../../api/apiClient';

const PERSONAL_CODES = new Set([
  'my_attendance_toggle',
  'my_leave_balance',
  'my_daily_call_summary',
  'my_call_today',
  'my_contracts_month',
  'my_meeting_today',
  'my_region_recommended',
  'my_settlement_owed_pine',
  'my_settlement_owed_dot',
  'my_settlement_owed_lnj',
]);

export function useMyPersonalWidgets({ visibleCodes = [], showToast, enabled = true }) {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [attendance, setAttendance] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [dailyCallSummary, setDailyCallSummary] = useState(null);
  const [callToday, setCallToday] = useState(null);
  const [contractsMonth, setContractsMonth] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [regionRecommended, setRegionRecommended] = useState(null);
  const [settlementOwedPine, setSettlementOwedPine] = useState(null);
  const [settlementOwedDot, setSettlementOwedDot] = useState(null);
  const [settlementOwedLnj, setSettlementOwedLnj] = useState(null);

  // 위젯별 월 state — chevron nav 사용 위젯만 (admin useAdminWidgets 패턴).
  //   my_contracts_month: 'YYYY-MM' 로 사용자가 선택한 월. undefined → 서버 기본(오늘 KST).
  const [monthMap, setMonthMap] = useState({});

  const visibleSet = new Set(visibleCodes);
  const need = (code) => visibleSet.has(code);

  const safe = useCallback(async (p) => {
    try { return await p; } catch (err) {
      console.error('[useMyPersonalWidgets] 호출 실패:', err);
      return { ok: false, data: null };
    }
  }, []);

  // monthMap 안정 키 — 위젯별 월 변경 시 refetch
  const monthMapKey = JSON.stringify(monthMap || {});

  // chevron 월 nav 지원 위젯 url 빌더 — base 에 ?month=YYYY-MM 또는 &month=YYYY-MM 부착
  const withMonth = (baseUrl, ym) => {
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return baseUrl;
    const sep = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${sep}month=${encodeURIComponent(ym)}`;
  };

  const fetchAll = useCallback(async () => {
    if (!enabled) return;
    const anyVisible = visibleCodes.some((c) => PERSONAL_CODES.has(c));
    if (!anyVisible) return;

    setLoading(true);
    const contractsBase = withMonth('/api/my/widgets/contracts-month', monthMap.my_contracts_month);
    const results = await Promise.all([
      need('my_attendance_toggle')      ? safe(apiClient.get('/api/attendance/today'))              : Promise.resolve({ ok: false }),
      need('my_leave_balance')          ? safe(apiClient.get('/api/my/leave-balance'))              : Promise.resolve({ ok: false }),
      need('my_daily_call_summary')     ? safe(apiClient.get('/api/my/daily-call-summary?days=10')) : Promise.resolve({ ok: false }),
      need('my_call_today')             ? safe(apiClient.get('/api/my/widgets/call-today'))         : Promise.resolve({ ok: false }),
      need('my_contracts_month')        ? safe(apiClient.get(contractsBase))                         : Promise.resolve({ ok: false }),
      need('my_meeting_today')          ? safe(apiClient.get('/api/my/widgets/meeting'))            : Promise.resolve({ ok: false }),
      need('my_region_recommended')     ? safe(apiClient.get('/api/my/widgets/region'))             : Promise.resolve({ ok: false }),
      need('my_settlement_owed_pine')   ? safe(apiClient.get('/api/my/settlement-owed/pine'))       : Promise.resolve({ ok: false }),
      need('my_settlement_owed_dot')    ? safe(apiClient.get('/api/my/settlement-owed/dot'))        : Promise.resolve({ ok: false }),
      need('my_settlement_owed_lnj')    ? safe(apiClient.get('/api/my/settlement-owed/lnj'))        : Promise.resolve({ ok: false }),
    ]);
    if (need('my_attendance_toggle'))      setAttendance(results[0].ok ? results[0].data : null);
    if (need('my_leave_balance'))          setLeaveBalance(results[1].ok ? results[1].data : null);
    if (need('my_daily_call_summary'))     setDailyCallSummary(results[2].ok ? results[2].data : null);
    if (need('my_call_today'))             setCallToday(results[3].ok ? results[3].data : null);
    if (need('my_contracts_month'))        setContractsMonth(results[4].ok ? results[4].data : null);
    if (need('my_meeting_today'))          setMeeting(results[5].ok ? results[5].data : null);
    if (need('my_region_recommended'))     setRegionRecommended(results[6].ok ? results[6].data : null);
    if (need('my_settlement_owed_pine'))   setSettlementOwedPine(results[7].ok ? results[7].data : null);
    if (need('my_settlement_owed_dot'))    setSettlementOwedDot(results[8].ok ? results[8].data : null);
    if (need('my_settlement_owed_lnj'))    setSettlementOwedLnj(results[9].ok ? results[9].data : null);

    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, safe, visibleCodes.join('|'), monthMapKey]);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, visibleCodes.join('|'), monthMapKey]);

  // chevron 월 nav setter — 위젯별 월 state 변경. YYYY-MM 형식 검증.
  const PERSONAL_MONTH_AWARE_CODES = new Set(['my_contracts_month']);
  const setMonthForCode = useCallback((code, ym) => {
    if (!PERSONAL_MONTH_AWARE_CODES.has(code)) return;
    if (!/^\d{4}-\d{2}$/.test(String(ym || ''))) return;
    setMonthMap((prev) => ({ ...prev, [code]: ym }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 출근/퇴근 토글 ─────────────────────────────────────────────────────────
  // 정책상 mutation 은 항상 self — as_user_id 부착 절대 금지. 토글 후 본인 출퇴근 상태를 다시 읽는다.
  // 2026-10-12 사용자 1a: 대시보드와 똑같이 **현재 위치를 확인해 주소를 함께** 보낸다. 위치를 못 받으면 막는다.
  //   전엔 위치 없이({}) 보내 출퇴근 주소가 비었다(김예현 60일 중 32일 — 엑셀 주소 빈칸).
  const currentAddressForWidget = useCallback(() => fetchCurrentAddress({
    setAddressLoading: () => {}, setLocationUsable: () => {}, setCurrentAddress: () => {},
  }), []);

  const handleCheckIn = useCallback(async () => {
    setActionLoading(true);
    try {
      const address = await currentAddressForWidget();
      if (!address) {
        showToast?.('위치 권한 허용 및 현재 위치 확인 후 출근할 수 있습니다.', 'error');
        return;
      }
      const result = await apiClient.post('/api/attendance/check-in', { current_address: address });
      if (!result.ok) {
        const msg = result.data?.message || '출근 처리에 실패했습니다.';
        if (result.data?.code === 'LATE_REASON_REQUIRED') {
          showToast?.('지각 사유 입력이 필요합니다. 대시보드에서 출근해 주세요.', 'error');
        } else {
          showToast?.(msg, 'error');
        }
      } else {
        showToast?.(result.data?.message || '출근이 완료되었습니다.', 'success');
      }
      const att = await safe(apiClient.get('/api/attendance/today'));
      if (att.ok) setAttendance(att.data);
    } finally {
      setActionLoading(false);
    }
  }, [safe, showToast, currentAddressForWidget]);

  const handleCheckOut = useCallback(async () => {
    setActionLoading(true);
    try {
      const address = await currentAddressForWidget();
      if (!address) {
        showToast?.('위치 권한 허용 후 현재 위치 확인이 완료되어야 퇴근할 수 있습니다.', 'error');
        return;
      }
      const result = await apiClient.post('/api/attendance/check-out', { current_address: address });
      if (!result.ok) {
        showToast?.(result.data?.message || '퇴근 처리에 실패했습니다.', 'error');
      } else {
        showToast?.(result.data?.message || '퇴근이 완료되었습니다.', 'success');
      }
      const att = await safe(apiClient.get('/api/attendance/today'));
      if (att.ok) setAttendance(att.data);
    } finally {
      setActionLoading(false);
    }
  }, [safe, showToast, currentAddressForWidget]);

  return {
    loading,
    actionLoading,
    handleCheckIn,
    handleCheckOut,
    refresh: fetchAll,
    // 본인 위젯 데이터
    attendance,
    leaveBalance,
    dailyCallSummary,
    callToday,
    contractsMonth,
    meeting,
    regionRecommended,
    settlementOwedPine,
    settlementOwedDot,
    settlementOwedLnj,
    // chevron 월 nav (admin useAdminWidgets 와 같은 인터페이스)
    monthMap,
    setMonthForCode,
  };
}
